/**
 * Plain-language explanations of how every analysis number is computed — the ONE
 * place this copy lives. Consumed by the Excel export (methodology block on the
 * Dimension Summary sheet) AND the admin chart info tooltips, so the two can never
 * disagree. Every threshold is interpolated from the canonical constants in
 * analysis-config.ts, so if a number changes here the copy follows automatically —
 * no drift, nothing misleading. See stats.ts for the exact implementation and
 * contract/SPEC.md § Statistics.
 */
import {
  MEAN_MIDPOINT,
  SD_MIDPOINT,
  HIGH_DIVERGENCE_SD,
  BENCHMARK_DELTA_THRESHOLD,
  MIN_RELIABLE_N,
  SCALE_MIN,
  SCALE_MAX,
} from './analysis-config.ts';

// Rendered as-is in the copy, e.g. "3.0", "1.0", "1.2", "0.2".
const MEAN_MID = MEAN_MIDPOINT.toFixed(1);
const SD_MID = SD_MIDPOINT.toFixed(1);
const HIGH_DIV = HIGH_DIVERGENCE_SD.toFixed(1);
const DELTA = BENCHMARK_DELTA_THRESHOLD.toFixed(1);
const LOW_N = String(MIN_RELIABLE_N);
const SCALE = `${SCALE_MIN}–${SCALE_MAX}`;

/** The four quadrant names — identical to QUAD_LABEL (api) / QUADRANT_META (web). */
export const QUADRANT_LABELS = {
  contested_gaps: 'Contested Gaps',
  perception_gaps: 'Perception Gaps',
  foundation_priorities: 'Foundation Priorities',
  consistent_strengths: 'Consistent Strengths',
} as const;

/**
 * Ordered lines for the Excel "how these numbers are calculated" block.
 * First entry is the title; the rest are one-line definitions.
 */
export const METHODOLOGY_NOTES: string[] = [
  'How these numbers are calculated — identical for every engagement',
  `Scale — every question is answered on a ${SCALE} maturity scale (${SCALE_MIN} lowest, ${SCALE_MAX} highest).`,
  'Mean — the average of all answers that count toward the dimension. Blank when there are no answers.',
  'Median — the middle answer (50th percentile, linear interpolation).',
  `SD (standard deviation) — sample standard deviation (divides by n−1); it measures how much respondents disagree. Blank when fewer than 2 answers.`,
  'n — the number of distinct respondents who answered at least one question in the dimension (not the number of answers).',
  'Skipped answers — a blank / skipped question is excluded from every statistic (never counted as 0 or as a middle value); it simply lowers n and the answer count. A mean is never dragged toward any value by a skip.',
  `Low n — when fewer than ${LOW_N} respondents answered, the mean is flagged "indicative only": it is computed the same way but rests on too few people to read confidently.`,
  'Distribution — the count and share of answers at each level 1 to 5.',
  `Quadrant — each dimension is placed by its mean (maturity, split at ${MEAN_MID}) and its SD (agreement, split at ${SD_MID}): ` +
    `mean ≥ ${MEAN_MID} & SD ≤ ${SD_MID} = ${QUADRANT_LABELS.consistent_strengths}; ` +
    `mean ≥ ${MEAN_MID} & SD > ${SD_MID} = ${QUADRANT_LABELS.perception_gaps}; ` +
    `mean < ${MEAN_MID} & SD ≤ ${SD_MID} = ${QUADRANT_LABELS.foundation_priorities}; ` +
    `mean < ${MEAN_MID} & SD > ${SD_MID} = ${QUADRANT_LABELS.contested_gaps}.`,
  `High Divergence — a question is flagged when its SD is above ${HIGH_DIV}, i.e. respondents strongly disagree on it.`,
  'Cross-LOB Range — for a dimension, the highest LOB mean minus the lowest LOB mean.',
  `Benchmark delta — the engagement mean minus the Wipro target; shown only when the gap is larger than ±${DELTA}.`,
  'Multiple dimensions — a question can belong to more than one dimension; its answer is counted toward every dimension it maps to, so the same answer can feed more than one dimension’s statistics.',
];

export type ChartKey =
  | 'radar'
  | 'quadrant'
  | 'heatmap'
  | 'summary'
  | 'dimensionDistribution'
  | 'questionDistribution'
  | 'crossLob';

export interface ChartExplanation {
  /** What the chart shows. */
  what: string;
  /** How the numbers behind it are computed. */
  how: string;
}

/** Per-chart plain-language help for the admin dashboard info tooltips. */
export const CHART_HELP: Record<ChartKey, ChartExplanation> = {
  radar: {
    what: `The mean maturity score (${SCALE}) for each of the 15 dimensions, at the current filter.`,
    how: 'Each spoke is the average of all answers counted toward that dimension. A question that maps to several dimensions is counted in each of them.',
  },
  quadrant: {
    what: 'Each dimension positioned by how mature it is (mean) and how much respondents agree (SD).',
    how: `Horizontal = mean, split at ${MEAN_MID}; vertical = SD, split at ${SD_MID}. The four zones are ` +
      `${QUADRANT_LABELS.consistent_strengths} (high mean, low SD), ${QUADRANT_LABELS.perception_gaps} (high mean, high SD), ` +
      `${QUADRANT_LABELS.foundation_priorities} (low mean, low SD) and ${QUADRANT_LABELS.contested_gaps} (low mean, high SD).`,
  },
  heatmap: {
    what: `The mean score (${SCALE}) per dimension, shaded by maturity — deeper colour is higher.`,
    how: 'Same means as the radar; only the presentation differs. Colour is a visual scale of the mean, not a separate calculation.',
  },
  summary: {
    what: 'One row per dimension with its mean, median, SD, quadrant and respondent count, sorted worst-first by mean.',
    how: `Mean = average; median = 50th percentile; SD = sample standard deviation (n−1), blank when fewer than 2 answers; n = distinct respondents who answered in the dimension. Skipped questions are excluded (never counted as 0), so they only lower n. Rows with fewer than ${LOW_N} respondents are flagged as indicative only.`,
  },
  dimensionDistribution: {
    what: 'For the selected dimension, the share of answers at each maturity level from 1 to 5.',
    how: 'Every answer counted toward the dimension is bucketed by its level; each bar segment is that level’s count as a percentage of the total.',
  },
  questionDistribution: {
    what: 'Every question in a dimension with its answer spread, mean, median, SD and a divergence flag.',
    how: `Stats are computed per question. "High Divergence" appears when a question’s SD is above ${HIGH_DIV}, meaning respondents strongly disagree. Skipped answers are excluded (they lower n only); a question answered by fewer than ${LOW_N} respondents is flagged as indicative only.`,
  },
  crossLob: {
    what: 'Dimension means compared across lines of business (LOBs).',
    how: 'Each cell is that LOB’s mean for the dimension. Range = the highest LOB mean minus the lowest, showing how much LOBs differ.',
  },
};
